import type { ConversationResponse } from "@9amhealth/openapi";
import { AiChatError, AiChatStream, type ToolCall } from "@9amhealth/shared";
import { Cubit } from "blac-next";
import envVariables from "src/lib/envVariables";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { authenticationState } from "src/state/state";
import { ConversationService } from "./ConversationService";
import {
  ChatEventType,
  type ChatPrompt,
  type DebugOverrides,
  type LeapChatFileRef,
  type LeapChatMessage,
  type MessageSendError,
  llmModels,
  normalizeMessageError
} from "./types";
import { ChatErrorSimulator } from "./ChatErrorSimulator";
import ChatPromptManager from "./ChatPromptManager";
import ChatScrollManager from "./ChatScrollManager";
import {
  isEmptyMessage,
  isEmptyNonLocalMessage,
  messagesEqual,
  normalizeMessageText,
  sortMessages
} from "./utils";

export interface ConversationThreadState {
  messages: LeapChatMessage[];
  isRunning: boolean;
  isInitializing: boolean;
  activeUserMessageId: string | null;
  stickyBottom: boolean;
  prompts: Record<string, ChatPrompt>;
  escalated: boolean;
  escalationBannerIndex: number | null;
  isOffline: boolean;
  overrides?: DebugOverrides;
}

export default class ConversationThreadCubit extends Cubit<ConversationThreadState> {
  private service = ConversationService.getInstance();
  private abortController: AbortController | null = null;
  private messageQueue: LeapChatMessage[] = [];
  private unsubConversations: (() => void) | null = null;
  private unsubMessages: (() => void) | null = null;
  protected StreamClass: typeof AiChatStream = AiChatStream;

  private scrollManager = new ChatScrollManager({
    onStickyBottomChange: (value) => this.patch({ stickyBottom: value }),
    onScrolledToBottom: () => this.markMessagesAsRead(),
    getStickyBottom: () => this.state.stickyBottom
  });

  private promptManager = new ChatPromptManager({
    service: this.service,
    getMessages: () => this.state.messages,
    getConversation: () => this.conversation,
    getPrompts: () => this.state.prompts,
    onPromptsChange: (prompts) => this.patch({ prompts }),
    onEscalationChange: (escalated, escalationBannerIndex) => {
      this.patch({ escalated, escalationBannerIndex });
      if (escalated) {
        this.scrollManager.scrollToBottomAfterRender();
      }
    }
  });

  conversationId: string | undefined;

  constructor() {
    super({
      messages: [],
      isRunning: false,
      isInitializing: false,
      activeUserMessageId: null,
      stickyBottom: false,
      prompts: {},
      escalated: false,
      escalationBannerIndex: null,
      isOffline: typeof navigator !== "undefined" ? !navigator.onLine : false
    });
  }

  init = (conversationId: string): void => {
    this.conversationId = conversationId;
    this.service.setActiveConversation(conversationId);
    this.unsubConversations = this.service.onConversationsChanged(() => {
      this.promptManager.updateEscalationState();
    });

    this.unsubMessages = this.service.onNewMessage((convId, message) => {
      if (convId === this.conversationId) {
        this.handleIncomingWsMessage(message);
      }
    });

    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);

    void this.load();
  };

  dispose(): void {
    this.cancelStream();
    this.messageQueue = [];
    this.service.setActiveConversation(null);
    this.unsubConversations?.();
    this.unsubMessages?.();
    this.scrollManager.dispose();
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
  }

  private handleOnline = (): void => {
    this.patch({ isOffline: false });
  };

  private handleOffline = (): void => {
    this.patch({ isOffline: true });
  };

  // -- Load --

  private async load(): Promise<void> {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }

    const cached = await this.service.getCachedMessages(conversationId);

    const normalized = cached
      .filter((m) => !isEmptyMessage(m))
      .map((m) => {
        const err = normalizeMessageError(
          m.error as string | MessageSendError | undefined
        );
        return err !== m.error ? { ...m, error: err } : m;
      });

    if (normalized.length > 0) {
      this.patch({
        messages: sortMessages(normalized),
        isInitializing: false
      });
      this.markLastMessageReadIfNeeded(normalized);
      this.scrollToBottomAfterRender();
    }

    if (this.service.isMessageSyncFresh(conversationId)) {
      const currentMessages =
        normalized.length > 0 ? sortMessages(normalized) : this.state.messages;
      const prompts = this.promptManager.buildPromptsFromMessages(
        currentMessages,
        conversationId
      );
      this.patch({ isInitializing: false, prompts });
      this.promptManager.updateEscalationState();
      this.scrollToBottomAfterRender();
    } else {
      try {
        const remoteMessages = (
          await this.service.fetchMessages(conversationId)
        ).filter((m) => !isEmptyMessage(m));
        if (this.conversationId !== conversationId) {
          return;
        }

        const remoteIds = new Set(remoteMessages.map((m) => m.id));
        const failedMessages = (
          normalized.length > 0 ? normalized : this.state.messages
        ).filter((m) => m.error && !remoteIds.has(m.id));

        const allMessages = [...remoteMessages, ...failedMessages];
        const prompts = this.promptManager.buildPromptsFromMessages(
          allMessages,
          conversationId
        );
        this.patchMessagesIfChanged(allMessages, {
          isInitializing: false,
          prompts
        });
        this.promptManager.updateEscalationState();
        this.scrollToBottomAfterRender();

        void this.service.syncMessagesCache(conversationId, remoteMessages);
      } catch (e) {
        reportErrorSentry(e);
        this.patch({ isInitializing: false });
      }
    }

    const pending = this.service.consumePendingFirstMessage();
    if (pending) {
      void this.sendMessage(pending.text, pending.fileIds, pending.files);
    }
  }

  // -- Incoming messages (WebSocket) --

  private handleIncomingWsMessage(message: LeapChatMessage): void {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }

    if (this.state.isRunning) {
      this.messageQueue.push(message);
      return;
    }

    this.processIncomingMessage(message);
  }

  private processIncomingMessage(message: LeapChatMessage): void {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }

    if (isEmptyNonLocalMessage(message)) {
      return;
    }

    const existingIdx = this.state.messages.findIndex(
      (m) => m.id === message.id
    );
    if (existingIdx !== -1) {
      if (message.metadata) {
        const existing = this.state.messages[existingIdx];
        const patched: LeapChatMessage = {
          ...existing,
          metadata: message.metadata,
          escalationPrompted:
            message.escalationPrompted ?? existing.escalationPrompted,
          isGeneratedResponse:
            message.isGeneratedResponse ?? existing.isGeneratedResponse,
          senderName: message.senderName ?? existing.senderName,
          senderDescription:
            message.senderDescription ?? existing.senderDescription
        };
        const updated = this.state.messages.map((m, i) =>
          i === existingIdx ? patched : m
        );
        this.patch({ messages: updated });

        if (patched.escalationPrompted) {
          const newPrompts = this.promptManager.buildPromptsFromMessages(
            [patched],
            conversationId
          );
          if (Object.keys(newPrompts).length > 0) {
            this.patch({
              prompts: { ...this.state.prompts, ...newPrompts }
            });
          }
        }

        this.persistCurrentMessages();
      }
      return;
    }

    const shouldMarkUnread = !this.state.stickyBottom;
    const incoming = shouldMarkUnread ? { ...message, unread: true } : message;
    const updated = [...this.state.messages, incoming];

    this.patchMessagesIfChanged(updated);

    if (message.escalationPrompted) {
      const newPrompts = this.promptManager.buildPromptsFromMessages(
        [message],
        conversationId
      );
      if (Object.keys(newPrompts).length > 0) {
        this.patch({
          prompts: { ...this.state.prompts, ...newPrompts }
        });
      }
    }

    if (!shouldMarkUnread && message.role !== "user") {
      void this.service.markRead(conversationId, message.id);
    }
  }

  private drainMessageQueue(): void {
    const queued = this.messageQueue;
    this.messageQueue = [];
    for (const message of queued) {
      this.processIncomingMessage(message);
    }
    if (queued.length > 0) {
      this.persistCurrentMessages();
    }
  }

  // -- Helpers --

  private get conversation(): ConversationResponse | null {
    const { conversationId } = this;
    if (!conversationId) {
      return null;
    }
    return this.service.getConversation(conversationId);
  }

  private patchMessagesIfChanged(
    messages: LeapChatMessage[],
    extra?: Partial<ConversationThreadState>
  ): boolean {
    const sorted = sortMessages(messages);
    if (messagesEqual(this.state.messages, sorted) && !extra) {
      return false;
    }
    if (messagesEqual(this.state.messages, sorted) && extra) {
      this.patch(extra);
      return false;
    }
    this.patch({ messages: sorted, ...extra });
    this.markLastMessageReadIfNeeded(sorted);
    return true;
  }

  private persistCurrentMessages(): void {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }
    const messagesToPersist = this.state.messages.filter(
      (m) =>
        !isEmptyNonLocalMessage(m) &&
        !(m.error && m.role === "assistant" && m.localId === m.id)
    );
    void this.service.persistMessages(conversationId, messagesToPersist);
  }

  private markLastMessageReadIfNeeded(messages: LeapChatMessage[]): void {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }
    const last = messages[messages.length - 1];
    if (last && last.role !== "user") {
      void this.service.markRead(conversationId, last.id);
    }
  }

  // -- DOM / scroll --

  setDomElements = (elements: {
    scrollEl: HTMLDivElement | null;
    contentEl: HTMLDivElement | null;
    composerEl: HTMLDivElement | null;
  }): void => {
    this.scrollManager.setElements(elements);
  };

  scrollToBottom = (): void => {
    this.scrollManager.scrollToBottom();
  };

  private scrollToBottomAfterRender(): void {
    this.scrollManager.scrollToBottomAfterRender();
  }

  restoreScrollIfNeeded = (): void => {
    this.scrollManager.restoreScrollIfNeeded();
  };

  markMessagesAsRead = (): void => {
    const hasUnread = this.state.messages.some((m) => m.unread);
    if (!hasUnread) {
      return;
    }

    const { conversationId } = this;
    if (!conversationId) {
      return;
    }

    const updated = this.state.messages.map((m) =>
      m.unread ? { ...m, unread: undefined } : m
    );
    this.patch({ messages: updated });
    this.persistCurrentMessages();
  };

  updateNewMessagePositioning = (): void => {
    this.scrollManager.updateNewMessagePositioning();
  };

  // -- Submit & streaming --

  sendMessage = async (
    text: string,
    fileIds?: string[],
    files?: LeapChatFileRef[]
  ): Promise<void> => {
    const normalized = normalizeMessageText(text);
    if ((!normalized && !fileIds?.length) || this.state.isRunning) {
      return;
    }

    this.patch({ isRunning: true });
    const userMsgId = this.addNewUserMessage(normalized, files);
    const assistantMsgId = crypto.randomUUID();
    this.persistCurrentMessages();

    await this.executeStream(userMsgId, assistantMsgId, normalized, fileIds);
  };

  retryMessage = (messageId: string): void => {
    const msg = this.state.messages.find((m) => m.id === messageId);
    if (!msg?.error || this.state.isRunning) {
      return;
    }

    const fileIds = msg.files?.map((f) => f.fileId);
    const assistantMsgId = crypto.randomUUID();

    const retryMsg = {
      ...msg,
      error: undefined,
      isLocal: true,
      createdAt: Date.now()
    };
    const remaining = this.state.messages.filter((m) => m.id !== messageId);
    this.patch({
      isRunning: true,
      messages: [...remaining, retryMsg],
      activeUserMessageId: messageId
    });

    this.persistCurrentMessages();

    void this.executeStream(messageId, assistantMsgId, msg.text, fileIds);
  };

  deleteMessage = (messageId: string): void => {
    const msg = this.state.messages.find((m) => m.id === messageId);
    if (!msg?.error) {
      return;
    }

    const { conversationId } = this;
    if (!conversationId) {
      return;
    }

    this.patch({
      messages: this.state.messages.filter((m) => m.id !== messageId)
    });
    void this.service.deleteMessage(conversationId, messageId);
  };

  cancelStream = (): void => {
    this.abortController?.abort();
  };

  private async executeStream(
    userMsgId: string | undefined,
    assistantMsgId: string,
    text: string,
    fileIds?: string[]
  ): Promise<void> {
    if (!navigator.onLine) {
      this.markMessageError(
        userMsgId,
        assistantMsgId,
        new AiChatError("offline", "Device is offline")
      );
      this.patch({ isRunning: false });
      this.drainMessageQueue();
      return;
    }

    try {
      await this.streamResponse(assistantMsgId, text, fileIds, userMsgId);
    } catch (e) {
      this.handleStreamError(e, userMsgId, assistantMsgId);
    } finally {
      this.abortController = null;
      this.patch({ isRunning: false });
      this.drainMessageQueue();
    }
  }

  private async streamResponse(
    assistantMsgId: string,
    text: string,
    fileIds?: string[],
    userMsgId?: string
  ): Promise<void> {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }
    this.abortController = new AbortController();

    const simulatedError = ChatErrorSimulator.consume();
    if (simulatedError) {
      throw simulatedError;
    }

    const body: Record<string, unknown> = {
      contentValue: text,
      contentType: "text/plain",
      conversationId
    };
    if (fileIds?.length) {
      body.fileIds = fileIds;
    }

    if (!this.state.escalated) {
      this.addNewAssistantMessage(assistantMsgId);
    }

    let currentAssistantMsgId = assistantMsgId;
    let currentUserMsgId = userMsgId;
    let deliveredMarked = false;

    const stream = new this.StreamClass({
      url: `${envVariables.API_BASE_URL}/v1/ai/ama`,
      body,
      headers: {
        Authorization: `Bearer ${authenticationState.accessToken}`
      },
      signal: this.abortController.signal,
      overrides: this.state.overrides,
      onEventType: {
        [ChatEventType.EscalationPrompt]: (data) => {
          this.showPrompt(
            ChatEventType.EscalationPrompt,
            data,
            currentAssistantMsgId,
            conversationId
          );
        },
        [ChatEventType.EscalationEscalated]: () => {
          this.promptManager.handleExternalEscalation(conversationId);
        },
        "messaging.reply.start": (data) => {
          const { referenceMessageId, senderDisplayName, senderDisplayRole } =
            data as {
              referenceMessageId: string;
              senderDisplayName: string;
              senderDisplayRole: string;
            };
          if (currentUserMsgId) {
            this.updateMessageId(currentUserMsgId, referenceMessageId);
            currentUserMsgId = referenceMessageId;
            if (!deliveredMarked) {
              deliveredMarked = true;
              this.markUserMessageDelivered(referenceMessageId);
            }
          }
          this.patch({
            messages: this.state.messages.map((msg) =>
              msg.id === currentAssistantMsgId
                ? {
                    ...msg,
                    senderName: senderDisplayName,
                    senderDescription: senderDisplayRole
                  }
                : msg
            )
          });
        },
        "messaging.reply.end": (data) => {
          const { replyMessageId } = data as { replyMessageId: string };
          this.updateMessageId(currentAssistantMsgId, replyMessageId);
          currentAssistantMsgId = replyMessageId;
        },
        "messaging.reply.error": () => {
          this.handleReplyError(currentAssistantMsgId);
        }
      },
      onChunk: (fullText, toolCalls) => {
        this.patchAssistantMessage(currentAssistantMsgId, fullText, toolCalls);
      },
      onComplete: (fullText, toolCalls) => {
        this.patchAssistantMessage(
          currentAssistantMsgId,
          fullText,
          toolCalls.length > 0 ? toolCalls : undefined
        );
        if (!deliveredMarked && currentUserMsgId) {
          deliveredMarked = true;
          this.markUserMessageDelivered(currentUserMsgId);
        }
        this.persistCurrentMessages();
      }
    });

    await stream.start();
  }

  private addNewUserMessage(text: string, files?: LeapChatFileRef[]): string {
    const localId = crypto.randomUUID();
    this.patch({
      messages: [
        ...this.state.messages,
        {
          id: localId,
          localId,
          role: "user",
          text,
          files,
          createdAt: Date.now(),
          isLocal: true
        }
      ],
      activeUserMessageId: localId
    });
    return localId;
  }

  private addNewAssistantMessage(id?: string): string {
    const localId = id ?? crypto.randomUUID();
    this.patch({
      messages: [
        ...this.state.messages,
        {
          id: localId,
          localId,
          role: "assistant",
          text: "",
          createdAt: Date.now(),
          isGeneratedResponse: true
        }
      ]
    });
    return localId;
  }

  private updateMessageId(oldId: string, newId: string): void {
    if (oldId === newId) {
      return;
    }

    const currentMsg = this.state.messages.find((m) => m.id === oldId);
    if (!currentMsg) {
      return;
    }

    const newMsg: LeapChatMessage = { ...currentMsg, id: newId };
    const newMessageList = this.state.messages.map((msg) =>
      msg.id === oldId ? newMsg : msg
    );

    const patch: Partial<ConversationThreadState> = {
      messages: newMessageList
    };

    const oldPrompt = this.state.prompts[oldId];
    if (oldPrompt) {
      const updatedPrompt = { ...oldPrompt, messageId: newId };
      const { [oldId]: _, ...rest } = this.state.prompts;
      patch.prompts = { ...rest, [newId]: updatedPrompt };
    }

    this.patch(patch);
  }

  private patchAssistantMessage(
    messageId: string,
    text: string,
    toolCalls?: ToolCall[]
  ): void {
    this.patch({
      messages: this.state.messages.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              text,
              toolCalls: toolCalls?.length ? [...toolCalls] : undefined
            }
          : msg
      )
    });
  }

  private handleStreamError(
    e: unknown,
    userMsgId: string | undefined,
    assistantMsgId: string
  ): void {
    if (e instanceof DOMException && e.name === "AbortError") {
      return;
    }
    reportErrorSentry(e);
    const chatError =
      e instanceof AiChatError
        ? e
        : new AiChatError(
            "unknown",
            e instanceof Error ? e.message : String(e)
          );
    this.markMessageError(userMsgId, assistantMsgId, chatError);
  }

  private handleReplyError(assistantMsgId: string): void {
    const error: MessageSendError = { code: "reply_error" };
    this.patch({
      messages: this.state.messages.map((m) =>
        m.id === assistantMsgId ? { ...m, error } : m
      )
    });
    this.persistCurrentMessages();
  }

  private markMessageError(
    userMsgId: string | undefined,
    assistantMsgId: string,
    chatError: AiChatError
  ): void {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }

    if (chatError.code === "auth") {
      void authenticationState.refreshAccessToken().catch(reportErrorSentry);
    }

    const sendError: MessageSendError = {
      code: chatError.code,
      statusCode: chatError.statusCode
    };
    const updated = this.state.messages
      .filter((msg) => msg.id !== assistantMsgId)
      .map((msg) =>
        msg.id === userMsgId ? { ...msg, error: sendError } : msg
      );
    this.patch({ messages: updated });
    this.persistCurrentMessages();
  }

  private markUserMessageDelivered(messageId: string): void {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }
    this.patch({
      messages: this.state.messages.map((m) =>
        m.id === messageId ? { ...m, isLocal: false } : m
      )
    });
    this.persistCurrentMessages();
  }

  // -- Prompts --

  showPrompt = (
    eventType: string,
    eventData: unknown,
    messageId: string,
    conversationId: string
  ): void => {
    this.promptManager.showPrompt(
      eventType,
      eventData,
      messageId,
      conversationId
    );
  };

  selectPromptOption = (messageId: string, value: string): void => {
    this.promptManager.selectPromptOption(messageId, value);
  };

  // -- Debug --

  updateDebugOptions = (options: {
    model?: (typeof llmModels)[number] | undefined;
    temperature?: number | undefined;
    reasoningEffort?: DebugOverrides["reasoningEffort"];
    systemPrompt?: string | undefined;
  }): void => {
    this.patch({
      overrides: {
        ...this.state.overrides,
        ...("model" in options && {
          llmModel: options.model
            ? {
                llmService: options.model.llmService,
                modelName: options.model.modelName
              }
            : undefined
        }),
        ...("temperature" in options && {
          temperature: options.temperature
        }),
        ...("reasoningEffort" in options && {
          reasoningEffort: options.reasoningEffort
        }),
        ...("systemPrompt" in options && {
          systemPrompt: options.systemPrompt
        })
      }
    });
  };

  getMessagesByMetadata(
    key: string,
    value: unknown,
    sortByDate?: boolean
  ): LeapChatMessage[] {
    const matches = this.state.messages.filter(
      (m) => m.metadata?.[key] === value
    );
    if (sortByDate) {
      matches.sort((a, b) => a.createdAt - b.createdAt);
    }
    return matches;
  }
}
